import React from 'react';
import DOMPurify from 'dompurify';
import * as ReactIntl from '../../node_modules/react-intl/index.js';

export * from '../../node_modules/react-intl/index.js';

const REACT_INTL_CONTEXT_KEY = '__FORTRESS_REACT_INTL_CONTEXT__';
const REACT_INTL_VALUE_KEY = '__FORTRESS_REACT_INTL_VALUE__';
const REACT_INTL_CACHE_KEY = '__FORTRESS_REACT_INTL_CACHE__';
const DEFAULT_TEXT_COMPONENT = React.Fragment;
const DEFAULT_INTL_CONFIG = {
  defaultLocale: 'en',
  defaultFormats: {},
  fallbackOnEmptyString: true,
  formats: {},
  messages: {},
  textComponent: DEFAULT_TEXT_COMPONENT,
};

// eslint-disable-next-line no-undef
const getGlobalScope = () => globalThis || {};

const getGlobalIntlContext = () => {
  const scope = getGlobalScope();

  if (!scope[REACT_INTL_CONTEXT_KEY]) {
    scope[REACT_INTL_CONTEXT_KEY] = React.createContext(null);
  }

  return scope[REACT_INTL_CONTEXT_KEY];
};

const getGlobalIntlCache = () => {
  const scope = getGlobalScope();

  if (!scope[REACT_INTL_CACHE_KEY]) {
    scope[REACT_INTL_CACHE_KEY] = ReactIntl.createIntlCache();
  }

  return scope[REACT_INTL_CACHE_KEY];
};

const getFallbackIntl = () => {
  const scope = getGlobalScope();

  if (!scope[REACT_INTL_VALUE_KEY]) {
    scope[REACT_INTL_VALUE_KEY] = ReactIntl.createIntl(
      DEFAULT_INTL_CONFIG,
      getGlobalIntlCache(),
    );
  }

  return scope[REACT_INTL_VALUE_KEY];
};

const shallowEqual = (objA, objB) => {
  if (objA === objB) {
    return true;
  }

  if (!objA || !objB) {
    return false;
  }

  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  return aKeys.every(
    (key) =>
      objA[key] === objB[key] &&
      Object.prototype.hasOwnProperty.call(objB, key),
  );
};

const processIntlConfig = (config) => {
  const result = {
    locale: config.locale,
    timeZone: config.timeZone,
    fallbackOnEmptyString: config.fallbackOnEmptyString,
    formats: config.formats,
    textComponent: config.textComponent,
    messages: config.messages,
    defaultLocale: config.defaultLocale,
    defaultFormats: config.defaultFormats,
    wrapRichTextChunksInFragment: config.wrapRichTextChunksInFragment,
    defaultRichTextElements: config.defaultRichTextElements,
  };

  if (config.onError !== undefined) {
    result.onError = config.onError;
  }

  if (config.onWarn !== undefined) {
    result.onWarn = config.onWarn;
  }

  return result;
};

const getDefinedProps = (props) =>
  Object.keys(props).reduce((definedProps, key) => {
    if (props[key] !== undefined) {
      definedProps[key] = props[key];
    }

    return definedProps;
  }, {});

const renderFormattedValue = (intl, children, formattedValue) => {
  if (typeof children === 'function') {
    return children(formattedValue);
  }

  const Text = intl.textComponent || DEFAULT_TEXT_COMPONENT;

  return <Text>{formattedValue}</Text>;
};

const createFormattedComponent = (displayName, formatName) => {
  const FormattedComponent = ({ value, children, ...formatProps }) => {
    const intl = useIntl();
    const formattedValue = intl[formatName](value, formatProps);

    return renderFormattedValue(intl, children, formattedValue);
  };

  FormattedComponent.displayName = displayName;

  return FormattedComponent;
};

const createFormattedDateTimePartsComponent = (displayName, formatName) => {
  const FormattedPartsComponent = ({ value, children, ...formatProps }) => {
    const intl = useIntl();
    const date = typeof value === 'string' ? new Date(value || 0) : value;
    const parts = intl[formatName](date, formatProps);

    return children(parts);
  };

  FormattedPartsComponent.displayName = displayName;

  return FormattedPartsComponent;
};

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const INCREMENTABLE_UNITS = ['second', 'minute', 'hour'];

const canIncrement = (unit = 'second') => INCREMENTABLE_UNITS.includes(unit);

const selectRelativeTimeUnit = (seconds) => {
  const absValue = Math.abs(seconds);

  if (absValue < MINUTE) {
    return 'second';
  }

  if (absValue < HOUR) {
    return 'minute';
  }

  if (absValue < DAY) {
    return 'hour';
  }

  return 'day';
};

const getDurationInSeconds = (unit) => {
  switch (unit) {
    case 'second':
      return 1;
    case 'minute':
      return MINUTE;
    case 'hour':
      return HOUR;
    default:
      return DAY;
  }
};

const valueToSeconds = (value, unit) => {
  if (!value) {
    return 0;
  }

  switch (unit) {
    case 'second':
      return value;
    case 'minute':
      return value * MINUTE;
    default:
      return value * HOUR;
  }
};

export const IntlContext = getGlobalIntlContext();
export const RawIntlProvider = IntlContext.Provider;

export const createIntl = ReactIntl.createIntl;
export const createIntlCache = ReactIntl.createIntlCache;

export const useIntl = () => {
  const intl = React.useContext(IntlContext);

  return intl ?? getGlobalScope()[REACT_INTL_VALUE_KEY] ?? getFallbackIntl();
};

export const IntlProvider = ({ children, ...props }) => {
  const cacheRef = React.useRef(createIntlCache());
  const prevConfigRef = React.useRef();
  const intlRef = React.useRef();
  const config = processIntlConfig({
    ...DEFAULT_INTL_CONFIG,
    ...getDefinedProps(props),
  });

  if (!prevConfigRef.current || !shallowEqual(prevConfigRef.current, config)) {
    prevConfigRef.current = config;
    intlRef.current = createIntl(config, cacheRef.current);
    getGlobalScope()[REACT_INTL_VALUE_KEY] = intlRef.current;
  }

  return <RawIntlProvider value={intlRef.current}>{children}</RawIntlProvider>;
};

IntlProvider.displayName = 'IntlProvider';

export const FormattedNumber = createFormattedComponent(
  'FormattedNumber',
  'formatNumber',
);
export const FormattedDate = createFormattedComponent(
  'FormattedDate',
  'formatDate',
);
export const FormattedTime = createFormattedComponent(
  'FormattedTime',
  'formatTime',
);
export const FormattedList = createFormattedComponent(
  'FormattedList',
  'formatList',
);
export const FormattedDisplayName = createFormattedComponent(
  'FormattedDisplayName',
  'formatDisplayName',
);

export const FormattedNumberParts = ({ value, children, ...formatProps }) => {
  const intl = useIntl();

  return children(intl.formatNumberToParts(value, formatProps));
};

FormattedNumberParts.displayName = 'FormattedNumberParts';

export const FormattedListParts = ({ value, children, ...formatProps }) => {
  const intl = useIntl();

  return children(intl.formatListToParts(value, formatProps));
};

FormattedListParts.displayName = 'FormattedListParts';

export const FormattedDateParts = createFormattedDateTimePartsComponent(
  'FormattedDateParts',
  'formatDateToParts',
);
export const FormattedTimeParts = createFormattedDateTimePartsComponent(
  'FormattedTimeParts',
  'formatTimeToParts',
);

const areFormattedMessagePropsEqual = (prevProps, nextProps) => {
  const { values, ...otherProps } = prevProps;
  const { values: nextValues, ...nextOtherProps } = nextProps;

  return (
    shallowEqual(values, nextValues) && shallowEqual(otherProps, nextOtherProps)
  );
};

const FormattedMessageComponent = ({
  id,
  description,
  defaultMessage,
  values,
  children,
  tagName,
  ignoreTag,
}) => {
  const intl = useIntl();
  const Component = tagName || intl.textComponent || DEFAULT_TEXT_COMPONENT;
  const nodes = intl.formatMessage(
    { id, description, defaultMessage },
    values,
    { ignoreTag },
  );

  if (typeof children === 'function') {
    return children(Array.isArray(nodes) ? nodes : [nodes]);
  }

  if (Component) {
    return <Component>{nodes}</Component>;
  }

  return <>{nodes}</>;
};

FormattedMessageComponent.displayName = 'FormattedMessage';

export const FormattedMessage = React.memo(
  FormattedMessageComponent,
  areFormattedMessagePropsEqual,
);

FormattedMessage.displayName = 'MemoizedFormattedMessage';

export const FormattedPlural = ({ value, other, children, ...props }) => {
  const intl = useIntl();
  const Text = intl.textComponent || DEFAULT_TEXT_COMPONENT;
  const formattedPlural = props[intl.formatPlural(value, props)] || other;

  if (typeof children === 'function') {
    return children(formattedPlural);
  }

  if (Text) {
    return <Text>{formattedPlural}</Text>;
  }

  return formattedPlural;
};

FormattedPlural.displayName = 'FormattedPlural';

export const FormattedDateTimeRange = ({
  from,
  to,
  children,
  ...formatProps
}) => {
  const intl = useIntl();
  const formattedValue = intl.formatDateTimeRange(from, to, formatProps);

  return renderFormattedValue(intl, children, formattedValue);
};

FormattedDateTimeRange.displayName = 'FormattedDateTimeRange';

const SimpleFormattedRelativeTime = ({
  value,
  unit,
  children,
  ...formatProps
}) => {
  const intl = useIntl();
  const formattedValue = intl.formatRelativeTime(value || 0, unit, formatProps);

  return renderFormattedValue(intl, children, formattedValue);
};

export const FormattedRelativeTime = ({
  value = 0,
  unit = 'second',
  updateIntervalInSeconds,
  ...props
}) => {
  if (updateIntervalInSeconds && !canIncrement(unit)) {
    throw new Error('Cannot schedule update with unit longer than hour');
  }

  const [prevUnit, setPrevUnit] = React.useState();
  const [prevValue, setPrevValue] = React.useState(0);
  const [currentValueInSeconds, setCurrentValueInSeconds] = React.useState(0);
  const updateTimer = React.useRef();

  if (unit !== prevUnit || value !== prevValue) {
    setPrevValue(value || 0);
    setPrevUnit(unit);
    setCurrentValueInSeconds(
      canIncrement(unit) ? valueToSeconds(value, unit) : 0,
    );
  }

  React.useEffect(() => {
    const clearUpdateTimer = () => {
      clearTimeout(updateTimer.current);
    };

    clearUpdateTimer();

    if (!updateIntervalInSeconds || !canIncrement(unit)) {
      return clearUpdateTimer;
    }

    const nextValueInSeconds = currentValueInSeconds - updateIntervalInSeconds;
    const nextUnit = selectRelativeTimeUnit(nextValueInSeconds);

    if (nextUnit === 'day') {
      return clearUpdateTimer;
    }

    const unitDuration = getDurationInSeconds(nextUnit);
    const prevInterestingValueInSeconds =
      nextValueInSeconds - (nextValueInSeconds % unitDuration);
    const nextInterestingValueInSeconds =
      prevInterestingValueInSeconds >= currentValueInSeconds
        ? prevInterestingValueInSeconds - unitDuration
        : prevInterestingValueInSeconds;
    const delayInSeconds = Math.abs(
      nextInterestingValueInSeconds - currentValueInSeconds,
    );

    if (currentValueInSeconds !== nextInterestingValueInSeconds) {
      updateTimer.current = setTimeout(
        () => setCurrentValueInSeconds(nextInterestingValueInSeconds),
        delayInSeconds * 1000,
      );
    }

    return clearUpdateTimer;
  }, [currentValueInSeconds, updateIntervalInSeconds, unit]);

  let currentValue = value || 0;
  let currentUnit = unit;

  if (
    canIncrement(unit) &&
    typeof currentValueInSeconds === 'number' &&
    updateIntervalInSeconds
  ) {
    currentUnit = selectRelativeTimeUnit(currentValueInSeconds);
    currentValue = Math.round(
      currentValueInSeconds / getDurationInSeconds(currentUnit),
    );
  }

  return (
    <SimpleFormattedRelativeTime
      value={currentValue}
      unit={currentUnit}
      {...props}
    />
  );
};

FormattedRelativeTime.displayName = 'FormattedRelativeTime';

export const FormattedHTMLMessage = ({
  tagName: Tag = 'span',
  id,
  defaultMessage,
  description,
  values,
  ...props
}) => {
  const intl = useIntl();
  const message = intl.formatMessage(
    { id, defaultMessage, description },
    values,
  );

  return (
    <Tag
      {...props}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }}
    />
  );
};

const getRelativeFormatUnit = (diffInSeconds) => {
  const absSeconds = Math.abs(diffInSeconds);

  if (absSeconds < 60) {
    return ['second', 1];
  }

  if (absSeconds < 60 * 60) {
    return ['minute', 60];
  }

  if (absSeconds < 60 * 60 * 24) {
    return ['hour', 60 * 60];
  }

  if (absSeconds < 60 * 60 * 24 * 30) {
    return ['day', 60 * 60 * 24];
  }

  if (absSeconds < 60 * 60 * 24 * 365) {
    return ['month', 60 * 60 * 24 * 30];
  }

  return ['year', 60 * 60 * 24 * 365];
};

export const FormattedRelative = ({ value, style = 'long', ...props }) => {
  const intl = useIntl();
  const diffInSeconds = (new Date(value).getTime() - Date.now()) / 1000;
  const [unit, divisor] = getRelativeFormatUnit(diffInSeconds);
  const amount = Math.round(diffInSeconds / divisor);
  const formatter = new Intl.RelativeTimeFormat(intl.locale, {
    numeric: 'auto',
    style,
  });

  return <span {...props}>{formatter.format(amount, unit)}</span>;
};

export const injectIntl = (WrappedComponent) => {
  const InjectIntl = React.forwardRef((props, ref) => {
    const intl = useIntl();

    return <WrappedComponent {...props} intl={intl} ref={ref} />;
  });

  InjectIntl.displayName = `InjectIntl(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;
  InjectIntl.WrappedComponent = WrappedComponent;

  return InjectIntl;
};

export default {
  ...ReactIntl,
  createIntl,
  createIntlCache,
  IntlContext,
  RawIntlProvider,
  IntlProvider,
  useIntl,
  FormattedDate,
  FormattedDateParts,
  FormattedDateTimeRange,
  FormattedDisplayName,
  FormattedHTMLMessage,
  FormattedList,
  FormattedListParts,
  FormattedMessage,
  FormattedNumber,
  FormattedNumberParts,
  FormattedPlural,
  FormattedRelative,
  FormattedRelativeTime,
  FormattedTime,
  FormattedTimeParts,
  injectIntl,
};
